import { useState, useMemo, useEffect, useRef } from "react";
import { useLanguage } from "../contexts/LanguageContext";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";
import { Button } from "./ui/button";
import { Checkbox } from "./ui/checkbox";
import { ScrollArea } from "./ui/scroll-area";
import { Badge } from "./ui/badge";
import { Label } from "./ui/label";
import { Calendar } from "./ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "./ui/popover";
import { cn } from "./ui/utils";
import { Loader2, ArrowRight, ArrowLeft, Check, Users } from "lucide-react";
import {
  FileText,
  Clock,
  Calendar as CalendarIcon,
  CalendarDays,
  ChevronRight,
  ChevronDown,
} from "lucide-react";
import { useSelector } from "react-redux";
import { RootState } from "../store";
import { useGetTemplatesQuery } from "../store/api/templates";
import { useApplyTemplateToProjectMutation } from "../store/api/tasks";
import { useGetProjectMembersQuery } from "../store/api/projects";
import { toast } from "sonner";
import { format } from "date-fns";
import { ja, enUS } from "date-fns/locale";
import { handleApiError } from "../utils/apiErrors";
import { Task, RepeatType } from "../types";

interface AddTasksFromTemplateModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  projectId: string;
}

export default function AddTasksFromTemplateModal({
  open,
  onOpenChange,
  projectId,
}: AddTasksFromTemplateModalProps) {
  const { t, language } = useLanguage();
  const dialogContentRef = useRef<HTMLDivElement>(null);
  const currentWorkspace = useSelector((state: RootState) => state.workspace.currentWorkspace);
  const currentUser = useSelector((state: RootState) => state.auth.user);
  const workspaceId = currentWorkspace?.id || "";
  
  // API calls
  const { data: taskTemplates = [], isLoading: templatesLoading } = useGetTemplatesQuery(
    { workspaceId },
    { skip: !workspaceId || !open }
  );
  const { data: projectMembers = [], isLoading: membersLoading } = useGetProjectMembersQuery(
    { workspaceId, projectId },
    { skip: !workspaceId || !projectId || !open }
  );
  const [applyTemplate, { isLoading: isApplying }] = useApplyTemplateToProjectMutation();
  
  const [step, setStep] = useState<'select' | 'configure'>('select');
  const [selectedTaskIds, setSelectedTaskIds] = useState<string[]>([]);
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set());
  
  // Configuration state
  const [baseDate, setBaseDate] = useState<Date>(new Date());
  const [selectedAssigneeIds, setSelectedAssigneeIds] = useState<string[]>(
    currentUser ? [currentUser.id] : []
  );

  // Build a map of taskId -> task for easy lookup
  const taskMap = useMemo(() => {
    const map = new Map<string, { task: Task; templateId: string; templateName: string }>();
    taskTemplates.forEach((template) => {
      template.tasks.forEach((task) => {
        map.set(task.id, { task, templateId: template.id, templateName: template.name });
      });
    });
    return map;
  }, [taskTemplates]);

  const handleToggleGroup = (groupId: string) => {
    setExpandedGroups((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(groupId)) {
        newSet.delete(groupId);
      } else {
        newSet.add(groupId);
      }
      return newSet;
    });
  };

  const handleToggleTask = (taskId: string) => {
    setSelectedTaskIds((prev) =>
      prev.includes(taskId)
        ? prev.filter((id) => id !== taskId)
        : [...prev, taskId]
    );
  };

  const handleSelectAllInGroup = (templateId: string, tasks: Task[]) => {
    const taskIds = tasks.map((t) => t.id);
    const allSelected = taskIds.every((id) => selectedTaskIds.includes(id));

    if (allSelected) {
      setSelectedTaskIds((prev) => prev.filter((id) => !taskIds.includes(id)));
    } else {
      setSelectedTaskIds((prev) => {
        const filtered = prev.filter((id) => !taskIds.includes(id));
        return [...filtered, ...taskIds];
      });
    }
  };

  const handleNext = () => {
    setStep('configure');
  };

  const handleBack = () => {
    setStep('select');
  };

  const handleClose = () => {
    onOpenChange(false);
  };

  // Reset form when modal closes
  useEffect(() => {
    if (!open) {
      setSelectedTaskIds([]);
      setExpandedGroups(new Set());
      setStep('select');
      setBaseDate(new Date());
      setSelectedAssigneeIds(currentUser ? [currentUser.id] : []);
    }
  }, [open, currentUser]);

  const handleToggleAssignee = (userId: string) => {
    setSelectedAssigneeIds((prev) =>
      prev.includes(userId)
        ? prev.filter((id) => id !== userId)
        : [...prev, userId]
    );
  };

  const handleAddTasks = async () => {
    if (selectedTaskIds.length === 0 || !workspaceId) return;
    
    try {
      await applyTemplate({
        workspaceId,
        projectId,
        data: {
          taskIds: selectedTaskIds,
          baseDate: format(baseDate, "yyyy-MM-dd"),
          assigneeIds: selectedAssigneeIds,
        },
      }).unwrap();
      
      toast.success(t('settings.tasksAddedSuccess', { count: selectedTaskIds.length }));
      handleClose();
    } catch (error) {
      console.error("Failed to apply tasks from template:", error);
      toast.error(t('settings.tasksAddedError', { count: selectedTaskIds.length }));
    }
  };

  const getRecurrenceLabel = (type: string) => {
    switch (type) {
      case "none":
        return t('tasks.none');
      case "daily":
        return t('tasks.daily');
      case "weekly":
        return t('tasks.weekly');
      case "monthly":
        return t('tasks.monthly');
      case "quarterly":
        return t('tasks.quarterly');
      case "semi-annually":
        return t('tasks.semiAnnually');
      case "yearly":
        return t('tasks.yearly');
      default:
        return type;
    }
  };

  const isLoading = templatesLoading || isApplying;

  // Scroll to top when step changes or modal opens
  useEffect(() => {
    if (open && dialogContentRef.current) {
      dialogContentRef.current.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, [open, step]);

  const selectedTemplates = useMemo(() => {
    return selectedTaskIds.map(taskId => taskMap.get(taskId)).filter(Boolean);
  }, [selectedTaskIds, taskMap]);

  const renderSelectStep = () => (
    <>
      <div className="h-[400px] overflow-y-auto pr-2 border border-gray-100 rounded-lg">
        <div className="space-y-3">
          {taskTemplates.length === 0 ? (
            <div className="text-center py-8">
              <FileText className="w-12 h-12 text-gray-400 mx-auto mb-3" />
              <p className="text-gray-600">
                {t('settings.noTemplates')}
              </p>
            </div>
          ) : (
            taskTemplates.map((template) => {
              const isExpanded = expandedGroups.has(template.id);
              const templateTaskIds = template.tasks.map((t) => t.id);
              const selectedCount = templateTaskIds.filter((id) =>
                selectedTaskIds.includes(id)
              ).length;

              return (
                <div
                  key={template.id}
                  className="bg-white rounded-lg border border-gray-200 overflow-hidden"
                >
                  <div className="p-3">
                    <div className="flex items-center gap-2">
                      <button
                        type="button"
                        onClick={() => handleToggleGroup(template.id)}
                        className="flex-shrink-0 p-1 hover:bg-gray-100 rounded"
                      >
                        {isExpanded ? (
                          <ChevronDown className="w-4 h-4 text-gray-500" />
                        ) : (
                          <ChevronRight className="w-4 h-4 text-gray-500" />
                        )}
                      </button>
                      
                      <div className="w-8 h-8 bg-purple-100 rounded flex items-center justify-center flex-shrink-0">
                        <FileText className="w-4 h-4 text-purple-600" />
                      </div>
                      
                      <button
                        type="button"
                        onClick={() => handleToggleGroup(template.id)}
                        className="flex-1 min-w-0 text-left"
                      >
                        <h3 className="text-sm text-gray-900 font-medium truncate">{template.name}</h3>
                        {template.description && (
                          <p className="text-xs text-gray-600 truncate">
                            {template.description}
                          </p>
                        )}
                      </button>
                      
                      <Badge variant="secondary" className="text-xs flex-shrink-0">
                        {template.tasks.length}{t('settings.items')}
                      </Badge>
                      
                      {template.tasks.length > 0 && (
                        <Button
                          type="button"
                          size="sm"
                          variant="outline"
                          className="flex-shrink-0 whitespace-nowrap"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleSelectAllInGroup(template.id, template.tasks);
                          }}
                        >
                          {selectedCount === template.tasks.length
                            ? t('settings.deselect')
                            : selectedCount > 0
                            ? `${selectedCount}/${template.tasks.length}`
                            : t('settings.selectAll')}
                        </Button>
                      )}
                    </div>
                  </div>

                  {isExpanded && template.tasks.length > 0 && (
                    <div className="border-t border-gray-200 bg-gray-50 max-h-60 overflow-y-auto">
                      {template.tasks.map((task) => {
                        const isSelected = selectedTaskIds.includes(task.id);

                        return (
                          <div
                            key={task.id}
                            className="flex items-start gap-3 p-3 hover:bg-gray-100 border-b border-gray-100 last:border-0 cursor-pointer"
                            onClick={() => handleToggleTask(task.id)}
                          >
                            <Checkbox
                              checked={isSelected}
                              onCheckedChange={() => handleToggleTask(task.id)}
                              onClick={(e) => e.stopPropagation()}
                              className="mt-0.5"
                            />
                            <div className="flex-1 min-w-0">
                              <div className="text-sm text-gray-900">{task.name}</div>
                              {task.description && (
                                <div className="text-xs text-gray-600 mt-0.5">
                                  {task.description}
                                </div>
                              )}
                              <div className="flex items-center gap-3 mt-1.5">
                                {task.daysUntilDue !== undefined && task.daysUntilDue > 0 && (
                                  <div className="text-xs text-gray-600">
                                    {t('settings.dueDays')}: {task.daysUntilDue}{t('settings.daysLater')}
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })
          )}
        </div>
      </div>

      <div className="flex items-center justify-between pt-4 border-t">
        <div className="text-sm text-gray-600">
          {selectedTaskIds.length > 0 && (
            <span>{selectedTaskIds.length}{t('settings.tasksSelected')}</span>
          )}
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            {t('common.cancel')}
          </Button>
          <Button
            onClick={handleNext}
            disabled={selectedTaskIds.length === 0}
          >
            {t('common.next')}
            <ArrowRight className="w-4 h-4 ml-1" />
          </Button>
        </div>
      </div>
    </>
  );

  const renderConfigureStep = () => (
    <>
      <div className="h-[450px] overflow-y-auto pr-2">
        <div className="space-y-6 pb-2">
          {/* Selected Tasks Confirmation */}
          <div>
            <Label className="text-sm font-semibold">{t('tasks.templateConfigStep')}</Label>
            <div className="mt-2 space-y-2 max-h-32 overflow-y-auto pr-1 border border-gray-100 rounded p-2">
              {selectedTemplates.map((item: any) => (
                <div
                  key={item.task.id}
                  className="flex items-start gap-2 p-2 bg-gray-50 rounded border border-gray-200"
                >
                  <Check className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" />
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium">{item.task.name}</div>
                    <div className="text-xs text-gray-600">
                      {item.templateName} • {getRecurrenceLabel(item.task.recurrence?.type || 'none')} • {t('settings.dueDays')}: {item.task.daysUntilDue}{t('settings.daysLater')}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Base Date Setting */}
          <div className="space-y-2">
            <Label className="text-sm font-semibold">{t('tasks.templateBaseDate')}</Label>
            <p className="text-xs text-gray-600">
              {t('tasks.templateBaseDateDesc')}
            </p>
            <Popover>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  className={cn(
                    "w-full justify-start text-left mt-2",
                    !baseDate && "text-muted-foreground"
                  )}
                >
                  <CalendarIcon className="mr-2 h-4 w-4" />
                  {baseDate ? format(baseDate, 'PPP', { locale: language === 'ja' ? ja : enUS }) : t('tasks.selectDate')}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0" align="start">
                <Calendar
                  mode="single"
                  selected={baseDate}
                  onSelect={(date) => date && setBaseDate(date)}
                  locale={language === 'ja' ? ja : enUS}
                />
              </PopoverContent>
            </Popover>
          </div>

          {/* Assignees Setting */}
          <div className="space-y-2">
            <Label className="text-sm font-semibold">{t('tasks.assignees')} ({t('common.optional')})</Label>
            <p className="text-xs text-gray-600">
              {t('tasks.templateAssigneesDesc')}
            </p>
            <div className="mt-2 space-y-2 max-h-48 overflow-y-auto pr-1">
              {membersLoading ? (
                <div className="flex justify-center p-4">
                  <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
                </div>
              ) : projectMembers.length === 0 ? (
                <div className="text-center py-4 text-xs text-gray-500 border border-dashed rounded">
                  {t('projects.noMembers')}
                </div>
              ) : (
                projectMembers.map((user) => (
                  <div
                    key={user.id}
                    className="flex items-center gap-2 p-2 rounded border border-gray-200 hover:bg-gray-50 cursor-pointer"
                    onClick={() => handleToggleAssignee(user.id)}
                  >
                    <Checkbox
                      checked={selectedAssigneeIds.includes(user.id)}
                      onCheckedChange={() => handleToggleAssignee(user.id)}
                      onClick={(e) => e.stopPropagation()}
                    />
                    <div className="flex items-center gap-2 flex-1">
                      <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center text-xs text-blue-700 font-semibold">
                        {user.name.charAt(0)}
                      </div>
                      <div className="min-w-0">
                        <div className="text-sm font-medium truncate">{user.name}</div>
                        <div className="text-xs text-gray-600 truncate">{user.email}</div>
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>

          {/* Configuration Note */}
          <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
            <p className="text-xs text-blue-900 leading-relaxed">
              💡 {t('tasks.templateConfigNote')}
            </p>
          </div>
        </div>
      </div>

      <div className="flex items-center justify-between pt-4 border-t">
        <Button variant="outline" onClick={handleBack} disabled={isApplying}>
          <ArrowLeft className="w-4 h-4 mr-1" />
          {t('common.back')}
        </Button>
        <div className="flex gap-2">
          <Button variant="outline" onClick={handleClose} disabled={isApplying}>
            {t('common.cancel')}
          </Button>
          <Button
            onClick={handleAddTasks}
            disabled={isApplying}
          >
            {isApplying ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : null}
            {t('tasks.createTasks', { count: selectedTaskIds.length })}
          </Button>
        </div>
      </div>
    </>
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent ref={dialogContentRef} className="max-w-2xl flex flex-col max-h-[90vh]">
        <DialogHeader>
          <DialogTitle>
            {step === 'select' ? t('settings.addTasksFromTemplate') : t('tasks.templateConfigTitle')}
          </DialogTitle>
          <DialogDescription>
            {step === 'select' ? t('settings.selectTemplateDesc') : t('tasks.templateConfigDesc')}
          </DialogDescription>
        </DialogHeader>

        {templatesLoading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-gray-400" />
          </div>
        ) : (
          <div className="flex-1 overflow-hidden">
            {step === 'select' ? renderSelectStep() : renderConfigureStep()}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
